import { useQuery } from "@tanstack/react-query";
import adminApiClient from "../../../api/admin";
import { Device, MachineCollection, NightModeAppearance } from "../../../Types";

export type FieldChangeFilter = {
  [K in keyof DeviceChangeLogFields]?: boolean;
};

export type FieldChangeQueryOptions = {
  limit?: number;
  clockId: string;
  fields: FieldChangeFilter;
  fromDate?: string;
};

export type DeviceChangeLogFields = Pick<
  Device,
  | "favorites"
  | "feeds"
  | "hideDev"
  | "registerProduct"
  | "timeOffset"
  | "timeFormat"
  | "klydo"
  | "brightness"
  | "sortDown"
  | "chimes"
  | "chimesVol"
  | "chimeMode"
  | "ticks"
  | "ticksVol"
  | "quietTimesOn"
  | "quietTimes"
  | "nightMode"
  | "nightModeAppearance"
  | "nightModeTimes"
  | "show"
  | "showPSF"
  | "show13Plus"
  | "showDials"
  | "freshHold"
  | "hourlyStrikes"
  | "hardVersion"
  | "machineCollections"
  | "curCollection"
  | "curCollections"
  | "curFavs"
  | "curFeed"
  | "mode"
  | "beta"
  | "isRetail"
  | "klydosSound"
  | "masterSoundMixer"
  | "playerVolume"
  | "screenState"
  | "tag"
  | "version"
  | "hiddenKlydos"
  | "isArtist"
  | "pendingKlydos"
  | "stayInFavorits"
>;

export enum NightModeOptions {
  AUTO = "AUTO",
  SCHEDULE = "SCHEDULE",
  OFF = "OFF",
}

export enum KlydoShowOptions {
  FIXED = "FIXED",
  DAY = "DAY",
  SIX_HOURS = "SIX_HOURS",
  SIXTY_MINUTES = "HOUR",
  FIFTEEN_MINUTES = "QUARTER",
  FIVE_MINUTES = "FIVE_MINUTES",
  ONE_MINUTE = "MINUTE",
}

export enum TimeFormat {
  AM_PM = "AM_PM",
  TWENTY_FOUR_HOURS = "TWENTY_FOUR_HOURS",
}

export type FieldChangeRow = {
  timestamp: Date;
  favorites?: string[];
  feeds?: string[];
  hideDev?: boolean;
  registerProduct?: boolean;
  timeOffset?: number;
  timeFormat?: TimeFormat;
  klydo?: string;
  brightness?: number;
  sortDown?: boolean;
  chimes?: boolean;
  chimesVol?: number;
  chimeMode?: string;
  ticks?: boolean;
  ticksVol?: number;
  quietTimesOn?: boolean;
  quietTimes?: { from: string; until: string };
  nightMode?: NightModeOptions;
  nightModeAppearance?: NightModeAppearance;
  nightModeTimes?: { from: string; until: string };
  show?: KlydoShowOptions;
  showPSF?: boolean;
  show13Plus?: boolean;
  showDials?: boolean;
  freshHold?: boolean;
  hourlyStrikes?: boolean;
  hardVersion?: { version: number; permanent?: boolean };
  machineCollections?: MachineCollection[];
  curCollection?: string;
  curCollections?: string;
  curFavs?: string;
  curFeed?: string;
  mode?: string;
  beta?: boolean;
  isRetail?: boolean;
  klydosSound?: boolean;
  masterSoundMixer?: {
    ALL?: boolean;
    ANIMATION?: boolean;
    CHIMES_AND_STRIKES?: boolean;
    TICKS?: boolean;
  };
  playerVolume?: number;
  screenState?: string;
  tag?: string;
  version?: number;
  hiddenKlydos?: Array<{ id: string; deletedAt: number }>;
  isArtist?: boolean;
  pendingKlydos?: Array<{
    id: string;
    insertTo: Array<{ mode: string; target: string | null }>;
    ttl: number | null;
    insertAt: number;
    playNow: boolean;
    attempts: number;
  }>;
  stayInFavorits?: boolean;
};

const useClockChangeLogs = (
  clockId: string,
  fields: FieldChangeFilter,
  enabled: boolean,
  limit: number = 100,
  fromDate?: string,
) => {
  return useQuery({
    queryKey: ["clock-change-logs", clockId, limit, fields, fromDate],
    queryFn: async () => {
      return adminApiClient.getClockChangeLogs(
        clockId,
        limit,
        fields,
        fromDate,
      );
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: enabled && !!clockId,
  });
};

export default useClockChangeLogs;
